<template>
    <div class="create-deal-process-modal">
        <ProcessModal
            data-qa="create-deal-process-modal-process"
            :is-open="isOpen"
            :title="$t('funnel.edit.process.create.deal.title')"
            :summary-text="$t('funnel.edit.process.create.deal.summary')"
            show-save
            @close-modal="closeModal"
            @update-config="validateCell"
        >
            <template #processParameters>
                <CampaignBuilderMergeableTextArea
                    v-model="deal.name"
                    data-qa="deal-name-input-value"
                    :has-legacy-format="true"
                    :funnel-id="funnelId"
                    :default-place-holder="$t('funnel.edit.process.create.deal.name.placeholder')"
                    :merge-field-categories="mergeFields"
                    class="create-deal-merge-field-input"
                />
                <DsInputField
                    v-model="deal.value"
                    type="number"
                    data-qa="deal-value-input-value"
                    class="deal-value-input-field"
                    :label="$t('funnel.edit.process.create.deal.value.placeholder')"
                />
                <DsMultiselect
                    v-model="selectedPipeline"
                    required
                    :force-invalid="isEmptyPipeline"
                    bind-value-only
                    label-prop="value"
                    value-prop="key"
                    :options="optionsPipeline"
                    :placeholder="$t('funnel.edit.process.create.deal.pipeline.placeholder')"
                    class="select-pipeline-dropdown"
                    @input="loadStageOptions"
                >
                    <template #help>
                        <span v-if="isEmptyPipeline" data-qa="empty-pipeline">
                            {{ $t('funnel.flow.field.required') }}
                        </span>
                    </template>
                </DsMultiselect>

                <DsMultiselect
                    v-model="selectedStage"
                    required
                    :force-invalid="isEmptyStage"
                    bind-value-only
                    label-prop="value"
                    value-prop="key"
                    :options="optionsStages"
                    :placeholder="$t('funnel.edit.process.create.deal.stage.placeholder')"
                    class="select-stages-dropdown"
                >
                    <template #help>
                        <span v-if="isEmptyStage" data-qa="empty-stage">
                            {{ $t('funnel.flow.field.required') }}
                        </span>
                    </template>
                </DsMultiselect>

                <DsMultiselect
                    v-model="selectedAssignedUser"
                    bind-value-only
                    label-prop="value"
                    value-prop="key"
                    :options="optionsUsers"
                    :placeholder="$t('funnel.edit.process.create.deal.assign.to.user.placeholder')"
                    class="select-users-dropdown"
                />

                <DsCheckbox
                    v-model="checkboxOnlyIfNoActive"
                    data-qa="checkbox-only-if-not-active"
                    :label="$t('funnel.edit.process.create.deal.checkbox.create.label')"
                />
            </template>
        </ProcessModal>
    </div>
</template>

<script>
import { getDealConfiguration } from './create.deal.api';
import ProcessModal from '../processModal/ProcessModal.vue';
import CampaignBuilderMergeableTextArea from '../../../whens/campaignBuilderMergeableTextArea/CampaignBuilderMergeableTextArea.vue';
import { getMergeFieldsForCB } from '../../../../configuration/task.flow.api';

export const ID_STR_PREFIX = 'id-';
export const DEFAULT_OWNER_ID = -1;

export default {
    ID_STR_PREFIX,
    components: { ProcessModal, CampaignBuilderMergeableTextArea },
    DEFAULT_OWNER_ID,
    props: {
        funnelId: {
            type: Number,
            default: 0,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                dealName: '',
                dealAmount: 0,
                stageId: '',
                userId: 0,
                onlyIfNoActive: false,
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            isEmptyStage: false,
            isEmptyPipeline: false,
            mergeFields: [],
            deal: {
                name: '',
                value: 0,
            },
            selectedPipeline: null,
            pipelineLists: [],
            optionsPipeline: [],
            selectedStage: null,
            optionsStages: [],
            selectedAssignedUser: null,
            optionsUsers: [],
            checkboxOnlyIfNoActive: false,
        };
    },
    computed: {
        tempDealName() {
            return this.cell?.value?.dealName ?? '';
        },

        tempDealValue() {
            return this.cell?.value?.dealAmount ?? 0;
        },

        tempStageId() {
            return this.cell?.value?.stageId ?? '';
        },

        tempUserId() {
            return (this.cell?.value?.userId && this.cell?.value?.userId !== '0') ? this.cell?.value?.userId : this.$options.DEFAULT_OWNER_ID;
        },

        tempOnlyIfNoActive() {
            return !!this.cell?.value?.onlyIfNoActive;
        },

        isValidStage() {
            return this.tempStageId && typeof this.tempStageId === 'string' && this.tempStageId.startsWith(this.$options.ID_STR_PREFIX);
        },
    },
    async mounted() {
        await this.loadCreateDealConfig();
        this.mergeFields = await getMergeFieldsForCB(this.funnelId);
    },
    methods: {
        async loadCreateDealConfig() {
            if (this.tempDealName) {
                this.deal.name = this.tempDealName;
            }

            if (this.tempDealValue) {
                this.deal.value = this.tempDealValue;
            }
            this.checkboxOnlyIfNoActive = this.tempOnlyIfNoActive;
            const { users = null, pipelines: { pipelines } = [] } = await getDealConfiguration();

            this.optionsUsers = await this.loadOptionsUsers(users);
            this.selectedAssignedUser = parseInt(this.tempUserId, 10);
            this.pipelineLists = [...pipelines];
            this.optionsPipeline = await this.loadOptionsPipeline(pipelines);

            if (this.isValidStage) {
                this.selectedStage = this.tempStageId.substring(this.$options.ID_STR_PREFIX.length);

                this.selectedPipeline = this.findParentId(this.selectedStage);

                if (this.selectedPipeline) {
                    this.optionsStages = pipelines.find((p) => p.id === this.selectedPipeline)?.stages.map((st) => ({
                        key: st.id,
                        value: st.name,
                    }));
                }
            }
        },

        async loadOptionsUsers(users) {
            let usersSorted = Object.entries(users).map(([id, name]) => ({
                key: parseInt(id, 10),
                value: name,
            })).sort((a, b) => {
                return a.value.localeCompare(b.value);
            });

            const assignUser = usersSorted.filter((user) => user.key === this.$options.DEFAULT_OWNER_ID);

            usersSorted = usersSorted.filter((sorted) => sorted.key !== this.$options.DEFAULT_OWNER_ID);
            usersSorted = [...assignUser, ...usersSorted];

            return usersSorted;
        },

        async loadOptionsPipeline(pipelines) {
            return pipelines?.map((p) => ({
                key: p.id,
                value: p.name,
            }));
        },

        validateCell() {
            if (this.selectedStage && this.selectedPipeline) {
                this.isEmptyStage = false;
                this.isEmptyPipeline = false;
                this.updateConfig();
            } else {
                this.isEmptyStage = !this.selectedStage;
                this.isEmptyPipeline = !this.selectedPipeline;
            }
        },

        async updateConfig() {
            try {
                const cell = { ...this.cell };

                cell.value = cell.value || {};
                cell.value.dealName = this.deal.name;
                cell.value.dealAmount = this.deal.value;
                cell.value.stageId = `${this.$options.ID_STR_PREFIX}${this.selectedStage}`;
                cell.value.userId = this.selectedAssignedUser;
                cell.value.onlyIfNoActive = this.checkboxOnlyIfNoActive;

                this.$emit('update-cell-value', { cell });
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.process.create.deal.error.creating.deal');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        closeModal() {
            this.$emit('close-modal');
        },
        loadStageOptions() {
            const index = this.selectedPipeline ?? -1;

            if (index !== -1) {
                const pipeline = this.pipelineLists.find((p) => p.id === index);

                this.optionsStages = pipeline?.stages?.map((st) => ({
                    key: st.id,
                    value: st.name,
                }));
            }
        },
        findParentId(stageId) {
            const pipelineWithStage = this.pipelineLists.find((p) => p.stages.some((s) => {
                return s.id === stageId;
            }));

            return pipelineWithStage?.id;
        },
    },
};
</script>

<style lang="scss" scoped>
.create-deal-merge-field-input {
    width: -webkit-fill-available;
    max-width: px-to-rem(590);
}

.mergeable-input.deal-name-input-class-value {
    margin-top: $spacing-250;
    margin-bottom: $spacing-300;
}

.deal-value-input-field {
    width: 50%;
}

</style>

<style lang="scss">
div.dropdown-reference > div > div.multiselect-tags > span > label {
    padding-top: 0;
}
</style>
